import { AxiosResponse } from "axios";
import {
    apiRequestSecureGet,
    apiRequestSecuredPost,
    apiRequestSecuredPut,
} from "../lib/common/api";
import {
    TAreThereNewMessagesType,
    TInvitationDecision,
    TInvitationDisplayStatus,
    TMessage,
    TMessageStatus,
    TMessages,
    TProcessStates,
} from "../lib/types/general";
import { useState } from "react";
import { useAuthContext } from "../contexts/auth-context";

const endpointAPIMessages = "users/messages";
const endpointAPIInvitations = "users/messages/invitation";
const endpointAPINewChatMessages = "users/chat/new_messages/";

type TInvitationStatusApiResponse = {
    data: {
        state: TInvitationDisplayStatus;
    };
};

export const getNotifications = async (
    userTokenId: string
): Promise<AxiosResponse<TMessages>> => {
    return await apiRequestSecureGet<TMessages>(
        endpointAPIMessages,
        userTokenId
    );
};

export const getNewChatMessages = async (
    userTokenId: string,
    withUserId: number
): Promise<AxiosResponse<{ messages: TAreThereNewMessagesType[] }>> => {
    return await apiRequestSecureGet<{ messages: TAreThereNewMessagesType[] }>(
        `${endpointAPINewChatMessages}${withUserId}`,
        userTokenId,
        {}
    );
};

const setInvitationState = async (
    userTokenId: string,
    invitationId: number,
    state: TInvitationDecision
): Promise<TInvitationDisplayStatus> => {
    const response = await apiRequestSecuredPut<TInvitationStatusApiResponse>(
        endpointAPIInvitations,
        userTokenId,
        {
            invitationId,
            state,
        }
    );
    return response.data.data.state;
};

export const getInvitationStatus = async (
    userTokenId: string,
    invitationId: number
): Promise<TInvitationDisplayStatus> => {
    const response = await apiRequestSecureGet<TInvitationStatusApiResponse>(
        `${endpointAPIInvitations}/${invitationId}/status`,
        userTokenId
    );
    return response.data.data.state;
};

export const acceptInvitation = async (
    userTokenId: string,
    invitationId: number
) => {
    return setInvitationState(userTokenId, invitationId, "accepted");
};

export const rejectInvitation = async (
    userTokenId: string,
    invitationId: number
) => {
    return setInvitationState(userTokenId, invitationId, "rejected");
};

export const setMessageState = async (
    message: TMessage,
    userTokenId: string,
    state: TMessageStatus
) => {
    return await apiRequestSecuredPut<any>(endpointAPIMessages, userTokenId, {
        messageId: message.id,
        state,
    });
};

export const setMessageRead = async (
    message: TMessage,
    userTokenId: string
) => {
    setMessageState(message, userTokenId, "read");
};

export const useMessages = (userId: number) => {
    const [sendInviteState, setSendInviteState] =
        useState<TProcessStates>("idle");

    const { getUserTokenId } = useAuthContext();

    const sendInvitation = async (note?: string) => {
        if (sendInviteState !== "idle") return;
        setSendInviteState("working");

        try {
            await apiRequestSecuredPost<any>(
                endpointAPIInvitations,
                await getUserTokenId(),
                {
                    toUserId: userId,
                    message: note,
                }
            );

            setSendInviteState("done");
        } catch (error) {
            console.error(error);
            setSendInviteState("error");
        }

        return "ok";
    };

    return {
        sendInviteState,
        sendInvitation,
    };
};
